import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const NewArrival = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [artworks, setArtworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const intervalRef = useRef(null);
    const location = useLocation();

    // Fetch new arrivals from API
    useEffect(() => {
        const fetchNewArrivals = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/new-arrivals`);
                if (response.ok) {
                    const data = await response.json();
                    setArtworks(data);
                } else {
                    console.error('Failed to fetch new arrivals');
                    // Fallback to empty array if API fails
                    setArtworks([]);
                }
            } catch (error) {
                console.error('Error fetching new arrivals:', error);
                // Fallback to empty array if API fails
                setArtworks([]);
            } finally {
                setLoading(false);
            }
        };

        fetchNewArrivals();
    }, []);

    useEffect(() => {
        // Check if URL has hash for this section
        if (location.hash === '#new-arrivals') {
            // Smooth scroll to the section with offset
            const element = document.getElementById('new-arrivals');
            if (element) {
                const headerElement = element.querySelector('header');
                if (headerElement) {
                    const offsetTop = headerElement.offsetTop - 100; // Adjust offset as needed
                    window.scrollTo({
                        top: offsetTop,
                        behavior: 'smooth'
                    });
                }
            }
        }
    }, [location.hash]);

    useEffect(() => {
        if (artworks.length > 0) {
            intervalRef.current = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % artworks.length);
            }, 3000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [artworks.length]);

    // Don't render if loading or no artworks
    if (loading) {
        return (
            <div id="new-arrivals" className="relative overflow-hidden flex flex-col" style={{ scrollMarginTop: '120px' }}>
                <header className="text-center pt-12 shrink-0 group">
                    <h1 className="relative inline-block text-5xl md:text-6xl font-serif text-gray-500 tracking-tight mb-4 pb-5">
                        New <br className="md:hidden" />Arrivals
                        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-gray-900 to-gray-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rounded-full" />
                    </h1>
                </header>
                <div className="text-center py-12">
                    <p className="text-gray-500">Loading new arrivals...</p>
                </div>
            </div>
        );
    }

    if (artworks.length === 0) {
        return null; // Don't render the section if there are no new arrivals
    }

    return (
        <div id="new-arrivals" className="relative overflow-hidden flex flex-col" style={{ scrollMarginTop: '120px' }}>
            <header className="text-center pt-12 shrink-0 group">
                <h1 className="relative inline-block text-5xl md:text-6xl font-serif text-gray-500 tracking-tight mb-4 pb-5">
                    New <br className="md:hidden" />Arrivals
                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-gray-900 to-gray-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out rounded-full" />
                </h1>
            </header>

            {/* Slides wrapper */}
            <div className="relative w-full overflow-hidden">
                <div
                    className="flex transition-transform duration-700 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                    {artworks.map((item) => (
                        <div key={item._id} className="w-full flex-shrink-0">
                            <Link to={item.url} className="flex flex-col-reverse md:flex-row w-full bg-white">

                                {/* Content (below in mobile, left in laptop) */}
                                <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col justify-center text-center">
                                    <h2 className="font-serif text-xl md:text-3xl font-medium text-gray-800 mb-2">
                                        Exclusive Hand Painting 
                                    </h2>
                                    <h1 className="text-3xl md:text-4xl font-serif text-gray-800 mb-6 leading-tight">
                                        {item.name}
                                    </h1>
                                    <div className="space-y-4 text-base text-gray-600">
                                        <p><span className="font-semibold">EHP:</span> {item.EHP}</p>
                                        <p><span className="font-semibold">Size:</span> {item.size}</p>
                                        <p><span className="font-semibold">Medium:</span> {item.medium}</p>
                                        <p><span className="font-semibold">Artist:</span> {item.artist}</p>
                                        <p>
                                            <span className="font-semibold">Price:</span>{" "}
                                            <span className="text-lg font-bold text-gray-800">{item.price}</span>
                                        </p>
                                    </div>
                                    <div className="mt-8">
                                        <span className="inline-block px-8 py-3 bg-gray-800 text-white text-center font-medium rounded-full shadow-md hover:bg-gray-700 transition-colors duration-300">
                                            View Details
                                        </span>
                                    </div>
                                </div>

                                {/* Image (above in mobile, right in laptop) */}
                                <div className="w-full md:w-1/2 p-4 md:p-8 flex items-center justify-center">
                                    <img
                                        src={item.image}
                                        alt={`${item.EHP} - ${item.name}`}
                                        className="w-full h-auto object-contain max-h-[400px] md:max-h-full"
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/400';
                                        }}
                                    />
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default NewArrival;
